import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";

const ConfettiParticle = ({ delay }: { delay: number }) => {
  const randomX = (Math.random() - 0.5) * 400;
  const randomY = (Math.random() - 0.5) * 400 - 100;
  const randomRotation = Math.random() * 360;
  const randomScale = Math.random() * 0.5 + 0.5;
  const colors = ["#5851D3", "#10B981", "#F59E0B", "#F0F1F7", "#858DA0"];
  const color = colors[Math.floor(Math.random() * colors.length)];
  const isCircle = Math.random() > 0.5;

  return (
    <motion.div
      className="absolute left-1/2 top-1/2 w-3 h-3"
      style={{ 
        backgroundColor: color,
        borderRadius: isCircle ? "50%" : "2px",
        x: "-50%",
        y: "-50%"
      }}
      initial={{ x: "-50%", y: "-50%", scale: 0, opacity: 1 }}
      animate={{ 
        x: `calc(-50% + ${randomX}px)`, 
        y: `calc(-50% + ${randomY}px)`, 
        rotate: randomRotation,
        scale: randomScale,
        opacity: [1, 1, 0]
      }}
      transition={{ 
        duration: 1.5, 
        delay: delay, 
        ease: "easeOut",
        opacity: { duration: 1.5, delay: delay, ease: "easeIn" }
      }}
    />
  );
};

export const OutroAnimation = () => {
  const [showConfetti, setShowConfetti] = useState(false);
  const particles = Array.from({ length: 40 });

  // Trigger confetti right when the logo finishes rolling in
  useEffect(() => {
    const timer = setTimeout(() => {
      setShowConfetti(true);
    }, 2400); // Logo animation takes ~2.4s total to land, so we trigger right after
    
    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="relative w-full h-[800px] flex flex-col items-center justify-center overflow-hidden font-sans" style={{ background: "linear-gradient(135deg, #12121A 0%, #1A1A2E 50%, #2D2B5A 100%)" }}>
      
      {/* Brighter Central Glow */}
      <motion.div 
        className="absolute w-[600px] h-[600px] bg-[#5851D3] rounded-full blur-[150px]" 
        initial={{ opacity: 0, scale: 0.5 }}
        animate={{ opacity: 0.35, scale: 1 }}
        transition={{ duration: 1.5, ease: "easeOut" }}
      />

      {/* Confetti container */}
      <div className="absolute inset-0 pointer-events-none z-0">
        {showConfetti && particles.map((_, i) => (
          <ConfettiParticle key={i} delay={0} />
        ))}
      </div>

      <div className="z-10 flex flex-col items-center justify-center gap-10">
        {/* Text Section */}
        <div className="flex flex-col items-center gap-3 text-center">
          <motion.h2 
            className="text-6xl md:text-7xl font-bold tracking-tight text-white drop-shadow-lg"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: "easeOut", delay: 0.2 }}
          >
            Your hiring. Your way.
          </motion.h2>
          
          <motion.p 
            className="text-3xl md:text-4xl text-[#CACDD8] font-medium mt-2"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: "easeOut", delay: 0.6 }}
          >
            Coming to your AI agent. <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#8B85FF] to-[#5851D3]">This April.</span>
          </motion.p>

          <motion.p 
            className="text-lg text-[#858DA0] font-medium mt-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, ease: "easeOut", delay: 1.0 }}
          >
            Request beta invites now.
          </motion.p>
        </div>

        {/* Rolling Logo */}
        <motion.div
          className="relative mt-6"
          initial={{ x: 800, rotate: 360, opacity: 0 }}
          animate={{ x: 0, rotate: 0, opacity: 1 }}
          transition={{ 
            duration: 1.4, 
            delay: 1.4, 
            type: "spring", 
            bounce: 0.3,
            opacity: { duration: 0.4, delay: 1.4 }
          }}
        >
          {/* Logo Glow */}
          <motion.div 
            className="absolute inset-0 bg-white rounded-full blur-[20px]"
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.5 }}
            transition={{ delay: 2.4, duration: 0.5 }}
          />
          <img 
            src="/cutshort-logo.png" 
            alt="Cutshort Logo" 
            className="relative w-32 h-32 object-contain drop-shadow-2xl"
          />
        </motion.div>
      </div>

    </div>
  );
};
